import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDatepicker, MatDatepickerInput, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatFormField } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { DateTime } from 'luxon';

@Component({
    selector: 'st-date-range-pager',
    imports: [FormsModule, MatDatepicker, MatDatepickerInput, MatFormField, MatIcon, MatInput],
    templateUrl: './date-range-pager.html',
    styleUrl: './date-range-pager.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-date-range-pager',
    },
})
export class DateRangePagerComponent {
    appService = inject(AppService);

    private store = inject(Store);

    public readonly isEnabled = input(true);
    readonly datePickerEnabled = input(false, {
        transform: coerceBooleanProperty,
    });

    readonly period = input<{ firstDate: DateTime; lastDate: DateTime }>({ firstDate: DateTime.now(), lastDate: DateTime.now().plus({ days: 7 }) });
    readonly dayCount = input<number>(7);

    readonly dateValue = signal<DateTime>(DateTime.now());
    readonly firstDate = linkedSignal(() => this.period()?.firstDate, {});
    readonly lastDate = linkedSignal(() => this.period()?.lastDate, {});

    public prev() {
        if (!this.isEnabled()) return;
        // recalculate date range, update label
        // we just need to substract 7 days from the first and the last
        const firstDate = this.firstDate().minus({ days: this.dayCount() });
        const lastDate = this.lastDate().minus({ days: this.dayCount() });

        if (!firstDate || !lastDate) return;
        this.store.dispatch(ViewStateActions.adjustDateRange({ firstDate, lastDate }));
    }

    readonly label = computed(() => {
        if (!this.firstDate() || !this.lastDate()) return '';
        if (this.dayCount() === 1) {
            return this.firstDate()?.toFormat('MMM d, yyyy');
        } else {
            // based on firstDate and lastDate, update the label.
            // take into account months (if they are different, years, etc. )

            // 1. if month is the same and year is the same
            if (this.firstDate().month === this.lastDate().month && this.firstDate().year === this.lastDate().year) {
                return `${this.firstDate().toFormat('MMM')} ${this.firstDate().day} - ${this.lastDate().day} , ${this.firstDate().toFormat('yyyy')}`;
            }
            // 2. if year is the same but month is different
            if (this.firstDate().year === this.lastDate().year && this.firstDate().month !== this.lastDate().month) {
                return `${this.firstDate().toFormat('MMM')} ${this.firstDate().day} - ${this.lastDate().toFormat('MMM')} ${this.lastDate().day} , ${this.firstDate().toFormat('yyyy')}`;
            }

            // 3. if both months and years are different
            if (this.firstDate().year !== this.lastDate().year && this.firstDate().month !== this.lastDate().month) {
                return `${this.firstDate().toFormat('MMM')} ${this.firstDate().day}, ${this.firstDate().toFormat('yyyy')} - ${this.lastDate().toFormat('MMM')} ${this.lastDate().day}, ${this.lastDate().toFormat('yyyy')}`;
            }
        }
    });

    public next() {
        if (!this.isEnabled()) return;
        // recalculate date range, update label
        // we just need to add 7 days to the first and the last
        const firstDate = this.firstDate().plus({ days: this.dayCount() });
        const lastDate = this.lastDate().plus({ days: this.dayCount() });

        if (!firstDate || !lastDate) return;
        this.store.dispatch(ViewStateActions.adjustDateRange({ firstDate, lastDate }));
    }

    public toggleDatePickerPopOver(picker: MatDatepicker<DateTime>) {
        if (!this.isEnabled()) return;
        if (this.datePickerEnabled()) {
            picker.open();
        }
    }

    dateValueChanged(event: MatDatepickerInputEvent<DateTime>) {
        if (!this.isEnabled() || !event?.value) return;
        const firstDate = event.value;
        const lastDate = firstDate.plus({ days: this.dayCount() });
        this.store.dispatch(ViewStateActions.adjustDateRange({ firstDate, lastDate }));
    }
}
